<script setup lang="ts">
import type {
  AllDocumentTypes,
  EventDocument,
  EventsDocument,
} from "~~/prismicio-types";
import type { ComputedRef } from "vue";
import type { ImageField } from "@prismicio/client";
import type {
  EmptyImageFieldImage,
  FilledImageFieldImage,
} from "@prismicio/types";
import { asImageSrc, isFilled } from "@prismicio/helpers";
import type { IListSamEvents } from "~~/types/calendarEvent";
import type { CalendarType } from "@schedule-x/calendar";
import defaultImg from "~~/public/logo.png";

definePageMeta({
  layout: "page",
});

const prismic = usePrismic();
const lang = useLang();
const { t } = useI18n();
const { isMobile } = useDevice();

const listEvents: IListSamEvents = reactive({ events: [] });
const { listCalendars } = useCalendarTypes()

// transform CalendarTypeEventList into Record<string, CalendarType for @schedule-x/calendar
const calendars: ComputedRef<Record<string, CalendarType>> = computed(() => {
  return listCalendars.value.reduce<Record<string, CalendarType>>(
    (acc, event) => {
      acc[event.id] = {
        colorName: event.id,
        lightColors: event.lightColors,
        darkColors: event.darkColors,
      };
      return acc;
    },
    {} as Record<string, CalendarType>,
  );
});

const HeaderPageTitle = defineAsyncComponent(() => import("~/components/pages/HeaderPageTitle.vue"));
const Loading = defineAsyncComponent(() => import("@/components/Layouts/Loading.vue"))
const Breadcrumbs = defineAsyncComponent(() => import("~/components/Layouts/Breadcrumbs.vue"));
const Fancybox = defineAsyncComponent(() => import("~/components//content/Fancybox.vue"));
const ScheduleSam = defineAsyncComponent(() => import("~/components/content/Scheduler.vue"));
const AsideSocialShare = defineAsyncComponent(() => import('@/components/Layouts/AsideSocialShare.vue'))

const currentDate = new Date();
const monthsAgo = new Date();
monthsAgo.setMonth(currentDate.getMonth() - 1);

const [{ data: events, error: eventsError, pending }, { data: agenda }] =
  await Promise.all([
    useAsyncData(
      "events",
      async () =>
        (await prismic.client.getAllByType<AllDocumentTypes>("event", {
          lang: lang.value,
          filters: [
            prismic.filter.dateAfter(
              "my.event.time_start",
              monthsAgo?.toISOString()?.split("T")[0] ?? "",
            ),
          ],
          orderings: {
            field: "my.event.time_start",
            direction: "asc",
          },
        })) as EventDocument[],
    ),
    useAsyncData(
      "agenda",
      async () =>
        (await prismic.client.getSingle("events", {
          lang: lang.value,
        })) as EventsDocument,
    ),
  ]);

if (eventsError.value) {
  throw createError({
    statusCode: eventsError.value?.statusCode,
    statusMessage: eventsError.value?.statusMessage,
  });
}

const richTextSerializer = useRichTextSerializer();
const { getKeyByValue } = useFindKeyFromArray();

events?.value?.forEach((event: EventDocument) => {
  listEvents?.events?.push({
    id: event.id,
    uid: event.uid,
    title: event.data.title as string,
    start: useFormatScheduleDate(event?.data?.time_start),
    end: event?.data.time_end
      ? useFormatScheduleDate(event?.data.time_end)
      : useFormatScheduleDate(event?.data.time_start),
    description: prismic.asText(event.data.resume) as string,
    location: event.data.place_event_txt as string,
    access_type_txt: event.data.access_type,
    access_type: getKeyByValue(
        listCalendars.value,
        "title",
        event.data.access_type,
        "id",
      ) ?? "allpublic",
  });
});

const imageBanner = computed<
  ImageField | FilledImageFieldImage | EmptyImageFieldImage | undefined
>(() => useBannerImage(agenda?.value?.data?.image_banner, isMobile));

const { title: metaTitle, description: metaDescription, image: metaImage } = usePrismicSeo({
  title: () => [
    agenda.value?.data.meta_title,
    agenda.value?.data.title,
  ],
  description: () => [
    agenda.value?.data.meta_description,
    agenda.value?.data.title,
  ],
  image: () => [agenda.value?.data.meta_image],
  defaultImage: defaultImg as string,
});

useSeo({
  title: metaTitle,
  description: metaDescription,
  image: metaImage,
});
</script>

<template>
  <section v-if="pending">
    <Loading />
  </section>
  <section
    v-if="agenda"
    class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8"
  >

    <header class="mb-8 overflow-hidden rounded-2xl bg-slate-900 text-white shadow-xl">
      <HeaderPageTitle :title="agenda.data.title" :image="imageBanner" />
    </header>

    <Breadcrumbs
      :list-ids="[agenda.id]"
      :current-uid="agenda.uid"
    />

    <div class="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
      <main class="lg:col-span-2 space-y-8 bg-white dark:bg-slate-900 p-6 sm:p-8 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm">
        <article class="prose dark:prose-invert max-w-none">
          <div data-content>
            <Fancybox>
              <prismic-rich-text
                  :field="agenda.data.content"
                  :serializer="richTextSerializer"
              />
            </Fancybox>
          </div>
        </article>

        <ClientOnly fallback-tag="span" :fallback="t('layout.loading')">
          <ScheduleSam
            :list-events="listEvents"
            :list-type-calendars="calendars"
          />
        </ClientOnly>
      </main>

      <aside class="space-y-6 lg:sticky lg:top-6">
        <div class="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm space-y-6">
          <AsideSocialShare :currentUrlPage="useRequestURL().href" />

          <div
              v-for="typeCal in listCalendars"
              :key="typeCal.id"
              class="rounded-xl shadow-md p-4 transition hover:scale-105 bg-green-100 border-l-4 border-green-500 text-green-700"
              :style="{
                backgroundColor: typeCal.lightColors.container,
                color: typeCal.lightColors.onContainer,
                borderColor: typeCal.lightColors.main,
              }"
          >
            <p class="text-lg mb-2 font-semibold">{{ typeCal.title }}</p>
            <p class="text-sm">{{ typeCal.description }}</p>
          </div>
        </div>
      </aside>
    </div>
  </section>
</template>

<style scoped></style>
